/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, { AxiosInstance, CancelToken } from 'axios'
import axiosRetry from 'axios-retry'
import { ProviderClient, LocationClient } from '@centivo/api-clients'
import { isEqual } from 'lodash'

import {
  getLocationServiceUrl,
  getProviderDomainUrlForContext,
} from 'src/context/functions/getAppConfig'
import { PCP_SERVICE_LINE_NAMES } from 'src/refactor/helpers/constants'

import { getStore } from 'src/context/functions/globalStore'
import { IFacility } from 'src/shared/types'
import {
  ServiceLine,
  GetProvidersFilter,
  GetProvidersSearch,
  Specialty,
  NominatimResponse,
  AppConfig,
  PlanNetwork,
  PlanNetworkMap,
  GetPracticeOpenSearch,
  GetProviderOpenSearch,
  SearchProvidersByPracticeIdParams,
  Specialties,
} from './types'
import { getIsPcpValue, isResultFulfiled, parseAxiosErrors } from './functions'

const REACT_APP_GEOLOCATION_API_ROOT =
  window.APP_CONFIG?.REACT_APP_GEOLOCATION_API_ROOT ||
  process.env.REACT_APP_GEOLOCATION_API_ROOT

const providerClient = new ProviderClient(getProviderDomainUrlForContext())
const locationClient = new LocationClient(
  getLocationServiceUrl(),
  process.env.CENTIVO_LOCATION_SECRET_NAME ||
    'location-service-centivo-portal-service-key',
  {}
)
const providerDomain = getProviderDomainUrlForContext()
const planManagerApi =
  window.APP_CONFIG?.REACT_APP_PLAN_MANAGER_API_URL ||
  process.env.REACT_APP_PLAN_MANAGER_API_URL

const MAX_PAGE_SIZE = 1000
class Client {
  private readonly instance: AxiosInstance

  private readonly planManagerInstance: AxiosInstance

  private nominatimInstance: AxiosInstance

  private providerClient: any

  private locationClient: any

  private networks: string[]

  private planNetworkMaps: PlanNetworkMap[]

  constructor(networks: string[] = [], planNetworkMaps: PlanNetworkMap[] = []) {
    this.instance = axios.create({
      baseURL: providerDomain,
      headers: {
        'Content-Type': 'application/json;charset=UTH-8',
        Accept: 'application/json',
      },
    })
    this.planManagerInstance = axios.create({
      baseURL: planManagerApi,
      headers: {
        'Content-Type': 'application/json;charset=UTH-8',
        Accept: 'application/json',
      },
    })
    axiosRetry(this.instance, {
      retries: 5,
      retryCondition: error => error.message === 'Network Error',
    })
    this.providerClient = providerClient
    this.locationClient = locationClient
    this.nominatimInstance = axios.create({
      baseURL: REACT_APP_GEOLOCATION_API_ROOT,
      headers: {
        'Content-Type': 'application/json;charset=UTH-8',
        Accept: 'application/json',
      },
      params: {
        format: 'json',
      },
    })

    this.planNetworkMaps = planNetworkMaps
    this.networks = networks
  }

  async getOESiteConfig(url: string) {
    const siteEnv = planManagerApi?.split('.').pop()

    const siteUrl = url.startsWith('localhost:')
      ? `${process.env.REACT_APP_DEBUG_SITE_URL ?? 'local'}.centivo.${siteEnv}`
      : url
    return parseAxiosErrors(async () =>
      this.planManagerInstance.get<AppConfig>(`/oe-config/sites/${siteUrl}`)
    )
  }

  async getOeNetworks(
    configNetworks: PlanNetwork[] | string,
    configRequireVpcModule?: boolean
  ) {
    if (!configNetworks) {
      console.error('Please configure the OE site networks correctly')

      return []
    }

    if (typeof configNetworks === 'string') {
      const { data } = await this.planManagerInstance.get<string[]>(
        '/network/getProviderDomainNetworkByPlanNetworkCodes',
        {
          params: {
            planNetworkCodes: configNetworks,
            requireVpcModule: configRequireVpcModule,
          },
        }
      )

      this.networks = data

      return data
    }

    const promiseArr = configNetworks.map(
      async ({ maps, planName, requireVpcModule }) => {
        const { data } = await this.planManagerInstance.get<string[]>(
          '/network/getProviderDomainNetworkByPlanNetworkCodes',
          {
            params: {
              planNetworkCodes: maps,
              requireVpcModule,
            },
          }
        )

        return {
          planName,
          networks: data,
        }
      }
    )

    const results = await Promise.allSettled(promiseArr)
    results.forEach(el => {
      if (!isResultFulfiled(el)) {
        console.error(el.reason)
      }
    })

    const response = results
      .filter(el => el.status === 'fulfilled')
      .map(el => (el as PromiseFulfilledResult<PlanNetworkMap>).value)

    this.planNetworkMaps = response

    return response
  }

  setPlanNetworks(selectedPlan?: string) {
    if (!this.planNetworkMaps || typeof this.planNetworkMaps === 'string') {
      console.error('Cannot set selected plan networks for client')

      return this.networks
    }

    this.networks =
      this.planNetworkMaps.find(({ planName }) => planName === selectedPlan)
        ?.networks || []

    return this.networks
  }

  async getServiceLines() {
    const serviceLines =
      (await this.providerClient.getServiceLines()) as ServiceLine[]

    const format = (serviceLine: ServiceLine) => ({
      label: serviceLine.name,
      codes: serviceLine.codes.join(','),
    })

    return {
      doctor: serviceLines
        .filter(sl => sl.isOeDoctor)
        .sort((x, y) => {
          if (PCP_SERVICE_LINE_NAMES.includes(x.name)) {
            return -1
          }
          if (PCP_SERVICE_LINE_NAMES.includes(y.name)) {
            return 1
          }

          return 0
        })
        .map(format),
      facility: serviceLines.filter(sl => sl.isOeFacility).map(format),
    }
  }

  async getSpecialties() {
    const results = (await this.providerClient.getSpecialties()) as
      | Specialty[]
      | null

    return results
      ? results.filter(item => item.isOe !== false)
      : ([] as Specialty[])
  }

  async getPracticesByPlaceId(
    params: any,
    cancelToken: CancelToken
  ): Promise<GetPracticeOpenSearch> {
    return await this.providerClient.getPracticesByPlaceId(params, {
      cancelToken,
    })
  }

  async getBatchPracticesByPlaceId(
    params: any,
    cancelToken: CancelToken
  ): Promise<GetPracticeOpenSearch> {
    const practices: GetPracticeOpenSearch['data'] = []
    let practicesResult: GetPracticeOpenSearch
    let page = 0

    do {
      // eslint-disable-next-line no-await-in-loop
      practicesResult = await this.getPracticesByPlaceId(
        {
          ...params,
          page,
        },
        cancelToken
      )

      practices.push(...practicesResult.data)
      page += 1
    } while (practices.length < practicesResult.count.totalPractices)

    return {
      ...practicesResult,
      data: practices,
    }
  }

  async getProvidersByPlaceId(
    params: any,
    cancelToken: CancelToken
  ): Promise<GetProviderOpenSearch> {
    return await this.providerClient.getProvidersByPlaceId(params, {
      cancelToken,
    })
  }

  async getBatchProvidersByPlaceId(
    params: any,
    cancelToken: CancelToken
  ): Promise<GetProviderOpenSearch> {
    const providers: GetProviderOpenSearch['data'] = []
    let providersResult: GetProviderOpenSearch
    let page = 0

    do {
      // eslint-disable-next-line no-await-in-loop
      providersResult = await this.getProvidersByPlaceId(
        {
          ...params,
          page,
        },
        cancelToken
      )

      providers.push(...providersResult.data)
      page += 1
    } while (providers.length < providersResult.count.total)

    return {
      ...providersResult,
      data: providers,
    }
  }

  async getPracticesAndProviders<Params extends GetProvidersSearch>(
    search: Params,
    filter: GetProvidersFilter,
    cancelToken: CancelToken,
    pageSize = MAX_PAGE_SIZE
  ) {
    const isPcp = getIsPcpValue(search.serviceLines)
    const specFilters = JSON.stringify(
      filter.specialtyFilters.map(code => ({ code, pcp: isPcp }))
    )
    const tasks = []

    const params = {
      ...search,
      gender: filter.gender === 'unknown' ? undefined : filter.gender,
      distance: filter.distance,
      virtualOptions: filter.withVirtualCare ? 'y, only' : undefined,
      isAcceptingNewPatients: filter.newPatients
        ? filter.newPatients
        : undefined,
      specialties: filter.specialtyFilters.length > 0 ? specFilters : undefined,
      networks: this.networks.join(','),
      pageSize,
      name: undefined,
    }

    if (pageSize === 0) {
      tasks.push(
        await this.getPracticesByPlaceId(
          {
            ...params,
            practiceName: search.name,
            page: 0,
          },
          cancelToken
        )
      )
    } else {
      tasks.push(
        await this.getBatchPracticesByPlaceId(
          {
            ...params,
            practiceName: search.name,
          },
          cancelToken
        )
      )
    }

    let providersResult: Partial<GetProviderOpenSearch> = {
      data: [],
      count: {
        total: 0,
        specialties: [],
        virtualOption: {
          n: 0,
          y: 0,
          only: 0,
        },
        gender: {
          m: 0,
          f: 0,
          unknown: 0,
        },
        isAcceptingNewPatients: 0,
      },
      specialtyFilters: [],
    }

    if (search.name) {
      if (pageSize === 0) {
        tasks.push(
          await this.getProvidersByPlaceId(
            {
              ...params,
              name: search.name,
            },
            cancelToken
          )
        )
      } else {
        tasks.push(
          await this.getBatchProvidersByPlaceId(
            {
              ...params,
              name: search.name,
            },
            cancelToken
          )
        )
      }
    }

    const results = await Promise.all(tasks)

    const practicesResult = results[0] as GetPracticeOpenSearch

    if (results[1]) {
      providersResult = results[1] as GetProviderOpenSearch
    }

    const specialtiesFilter: Specialties[] = []

    specialtiesFilter.push(...(practicesResult.specialtyFilters ?? []))

    const isSpecialtyAlreadyInSpecialtiesFilterArray = (
      specialtyObj: Specialties
    ) => {
      return specialtiesFilter.some(obj => isEqual(obj, specialtyObj))
    }

    providersResult.specialtyFilters?.forEach(specialtyObj => {
      if (!isSpecialtyAlreadyInSpecialtiesFilterArray(specialtyObj)) {
        specialtiesFilter.push(specialtyObj)
      }
    })

    return {
      specialtyFilters: specialtiesFilter,
      count: {
        total:
          practicesResult.count.totalProviders +
          (providersResult.count?.total ?? 0),
        totalCountingItems:
          practicesResult.count.totalPractices +
          (providersResult.count?.total ?? 0),
        requestCountProvider: providersResult.count,
        requestCountPractice: practicesResult.count,
        virtualOption: {
          n:
            (practicesResult.count?.virtualOption?.n ?? 0) +
            (providersResult.count?.virtualOption?.n ?? 0),
          y:
            (practicesResult.count?.virtualOption?.y ?? 0) +
            (providersResult.count?.virtualOption?.y ?? 0),
          only:
            (practicesResult.count?.virtualOption?.only ?? 0) +
            (providersResult.count?.virtualOption?.only ?? 0),
        },
        gender: {
          m:
            (practicesResult.count?.gender?.m ?? 0) +
            (providersResult.count?.gender?.m ?? 0),
          f:
            (practicesResult.count?.gender?.f ?? 0) +
            (providersResult.count?.gender?.f ?? 0),
          unknown:
            (practicesResult.count?.gender?.unknown ?? 0) +
            (providersResult.count?.gender?.unknown ?? 0),
        },
        isAcceptingNewPatients:
          practicesResult.count.isAcceptingNewPatients +
          (providersResult.count?.isAcceptingNewPatients ?? 0),
      },
      data: {
        practices: practicesResult.data ?? [],
        providers: providersResult.data ?? [],
      },
    }
  }

  async getProvidersByPracticeId<
    Params extends SearchProvidersByPracticeIdParams
  >(search: Params, filter: GetProvidersFilter) {
    const isPcp = getIsPcpValue(search.serviceLines)
    const specFilters = JSON.stringify(
      filter.specialtyFilters.map(code => ({ code, pcp: isPcp }))
    )

    const params = {
      isAcceptingNewPatients: filter.newPatients
        ? filter.newPatients
        : undefined,
      gender: filter.gender === 'unknown' ? undefined : filter.gender,
      pageSize: MAX_PAGE_SIZE,
      specialties: filter.specialtyFilters.length > 0 ? specFilters : undefined,
      virtualOptions: filter.withVirtualCare ? 'y, only' : undefined,
    }

    return await this.providerClient.getProvidersByPracticeId({
      ...search,
      ...params,
    })
  }

  async getFacilitiesByPlaceId(
    params: GetProvidersSearch &
      GetProvidersFilter & { page: number; pageSize: number },
    cancelToken?: CancelToken
  ) {
    return await this.providerClient.getFacilitiesByPlaceId(
      {
        ...params,
        distance: params.distance,
        serviceLines: params.serviceLines,
        specialties:
          params.specialtyFilters.length > 0
            ? params.specialtyFilters.join(',')
            : undefined,
        networks: this.networks.join(','),
      },
      { cancelToken }
    )
  }

  async getBatchFacilitiesByPlaceId(
    params: GetProvidersSearch & GetProvidersFilter,
    cancelToken: CancelToken
  ) {
    const facilities: IFacility[] = []
    let facilitiesResult: any
    let page = 0

    do {
      // eslint-disable-next-line no-await-in-loop
      facilitiesResult = await this.getFacilitiesByPlaceId(
        {
          ...params,
          page,
          pageSize: MAX_PAGE_SIZE,
        },
        cancelToken
      )

      facilities.push(...facilitiesResult.data)
      page += 1
    } while (facilities.length < facilitiesResult.count.total)

    return {
      data: {
        specialtyFilters: facilitiesResult.specialtyFilters,
        count: facilitiesResult.count,
        data: facilities,
      },
    }
  }

  async getZipCode(lat: number | string, lon: number | string) {
    const { data } = await this.nominatimInstance.get<NominatimResponse>(
      '/reverse',
      {
        params: {
          lat,
          lon,
        },
      }
    )

    return data.address.postcode
  }

  async getAutoSuggestPlaces(suggestion: string) {
    const response = await this.locationClient.getAutoSuggestPlaces(suggestion)

    return response?.predictions ?? []
  }
}

const store = getStore()

export const client = new Client(store?.networks, store?.planNetworkMaps)
